import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────
// Omnichannel inbox (Mensajes) — sidebar unread badge.
//
// Deliberately fetches ONLY the unread_count column: agents need the
// number in the sidebar without paying for conversation bodies. RLS
// already scopes rows to the caller's agency (super admins see all).
//
// ponytail: summing client-side beats an RPC while conversation counts
// are small — one smallint per row. If an agency ever passes a few
// thousand threads, swap the select for a SUM() RPC; the hook's shape
// won't change.
// ─────────────────────────────────────────────────────────────

export function useOmnichannelUnread() {
    const [totalUnread, setTotalUnread] = useState(0);

    const refresh = useCallback(async () => {
        if (!supabase) return;
        const { data, error } = await supabase
            .from('conversations')
            .select('unread_count')
            .gt('unread_count', 0);
        if (!error && Array.isArray(data)) {
            setTotalUnread(data.reduce((sum, c: { unread_count: number | null }) => sum + (c.unread_count || 0), 0));
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Realtime: any conversation change can move the count (new inbound
    // bumps it, opening a thread zeroes it). Unique channel name per mount —
    // supabase-js hands back the same instance for a given name, which breaks
    // across Fast Refresh / StrictMode remounts.
    useEffect(() => {
        if (!supabase) return;
        const channel = supabase
            .channel(`omnichannel_unread_${Math.random().toString(36).slice(2)}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations' }, () => { refresh(); })
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [refresh]);

    return { totalUnread, refresh };
}
